import { useCallback, useEffect, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

import type { DynamicView } from '../types/dynamic_views';

interface UseDynamicViewResult {
  view: DynamicView | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDynamicView(
  conversationId: number,
  orgId: string,
  viewId: number
): UseDynamicViewResult {
  const [view, setView] = useState<DynamicView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadView = useCallback(async () => {
    if (!viewId || !conversationId || !orgId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/dynamic-views/${viewId}/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const viewData = await response.json();
        setView(viewData);
      } else {
        setError("We couldn't load this view. Try again.");
      }
    } catch (err) {
      setError("We couldn't load this view. Try again.");
      console.error('Failed to load view:', err);
    } finally {
      setLoading(false);
    }
  }, [viewId, conversationId, orgId]);

  useEffect(() => {
    loadView();
  }, [loadView]);

  return { view, loading, error, refetch: loadView };
}
